import { useMutation, useQueryClient } from '@tanstack/react-query';
import { remixApi } from '@tectonic/api-client';
import { useStyleConfigV2 } from '@tectonic/elemason-components';
import { getErrorMessage } from '@tectonic/errors';
import { LocalStateKeys } from '@tectonic/types';
import { Input, InputField } from '@tectonic/uikit';
import clsx from 'clsx';
import { useEffect, useRef, useState, type FC } from 'react';
import { useDebounce } from 'use-debounce';
import { useToast } from '../../core/ElemasonEntry/Toast';
import { useSharedLocalState } from '../../hooks';
import { queryKeys } from '../../queryKeys';

import type { Cart, ElemasonOrderNoteWidget } from '@tectonic/types';

interface OrderNoteWidgetProps {
  widget: ElemasonOrderNoteWidget;
}

const STATE_KEY = LocalStateKeys.CART;

const OrderNoteWidget: FC<OrderNoteWidgetProps> = ({
  widget: { data, config },
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [style, className] = useStyleConfigV2(config?.container);
  const [inputStyle, inputClassName] = useStyleConfigV2(config?.input);
  const { sharedState: cart, setSharedState } =
    useSharedLocalState<Cart>(STATE_KEY);

  const noteRef = useRef(cart?.customerNote ?? '');
  const [orderNote, setOrderNote] = useState(cart?.customerNote ?? '');

  const [note] = useDebounce(orderNote, 1000);

  const updateMutation = useMutation({
    mutationFn: (text: string) => remixApi.updateCartNote(text),
    onSuccess: (cartData: Cart) => {
      setSharedState(cartData);
      queryClient.invalidateQueries({ queryKey: queryKeys.cart() });
    },
    onError: (error) => {
      const title = getErrorMessage(error, {}, 'Failed to update order note.');
      showToast({ title, duration: 5000 });
    },
  });

  useEffect(() => {
    if (note !== noteRef.current) {
      updateMutation.mutate(note);
      noteRef.current = note;
    }
  }, [note, noteRef]);

  return (
    <div style={style} className={clsx('flex', className)}>
      <Input style={inputStyle} className={inputClassName}>
        <InputField
          type="text"
          value={orderNote}
          placeholder={data?.placeholder}
          onChange={(event) => setOrderNote(event.target.value)}
        />
      </Input>
    </div>
  );
};

export { OrderNoteWidget };
